import React from "react";
import PageHeader from "./PageHeader";

/**
 * PageLayout — Ocean Blue Design System
 * 
 * Reusable layout wrapper that enforces:
 * - A fixed page height (preventing page-level scrollbars)
 * - Sticky headers and filters
 * - Flexbox column layout with an internally scrollable content area
 * 
 * @param {string} title - Page title
 * @param {string} subtitle - Page subtitle/description
 * @param {React.ReactNode} badge - Optional badge near the title (e.g. total counts)
 * @param {React.ReactNode} actions - Optional actions in the header (e.g. buttons)
 * @param {React.ReactNode} toolbar - Optional filter/search toolbar below the header
 * @param {string} className - Optional className override for the outer wrapper
 * @param {string} contentClassName - Optional className override for the scrollable content wrapper
 * @param {string} toolbarClassName - Optional className override for the toolbar wrapper
 * @param {React.ReactNode} children - The main content of the page (usually a Table or List)
 */
const PageLayout = ({
  title,
  subtitle,
  badge,
  actions,
  toolbar,
  className = "",
  contentClassName = "",
  toolbarClassName = "",
  children,
}) => {
  return (
    <div 
      className={[
        "bg-canvas-base h-[calc(100vh-120px)] font-sans flex flex-col overflow-hidden gap-2 px-2",
        className
      ].filter(Boolean).join(" ")}
    >
      {/* Page Header */}
      {(title || subtitle || badge || actions) && (
        <PageHeader
          title={title}
          subtitle={subtitle}
          badge={badge}
          actions={actions}
        />
      )}

      {/* Toolbar (Filters, Search, etc.) */}
      {toolbar && (
        <div 
          className={[
            "flex flex-row flex-wrap items-center justify-between gap-3 mb-1.5 w-full",
            toolbarClassName
          ].filter(Boolean).join(" ")}
        >
          {toolbar}
        </div>
      )}

      {/* Main Scrollable Content Area */}
      <div 
        className={[
          "flex-grow flex-1 min-h-0 flex flex-col overflow-hidden",
          contentClassName
        ].filter(Boolean).join(" ")}
      >
        {children}
      </div>
    </div>
  );
};

export default PageLayout;
